<template>
  <a-drawer
    :width="900"
    :visible="drawerVisible"
    :unmount-on-close="true"
    :mask-closable="false"
    hide-cancel
    ok-text="关闭"
    @ok="closeDrawer"
    @cancel="closeDrawer"
  >
    <template #title>
      <a-space>
        {{
          memberInfo?.nickname
            ? `${memberInfo.nickname}的客户列表`
            : '团队成员客户列表'
        }}
        <a-button type="primary" size="mini" @click="showAddCustomerDrawer">{{
          `给TA${t('followUpTable.operation.create')}`
        }}</a-button>
      </a-space>
    </template>

    <div style="margin-bottom: 16px">
      <a-descriptions
        :data="memberDescriptions"
        layout="inline-horizontal"
        :column="3"
      />
    </div>

    <a-table
      :columns="columns"
      :data="customerData"
      :pagination="pagination"
      :loading="loading"
      @page-change="handlePageChange"
    >
      <template #index="{ rowIndex }">
        {{ rowIndex + 1 + (pagination.current - 1) * pagination.pageSize }}
      </template>
      <template #isReport="{ record }">
        <a-tag :color="record.last_report_time ? 'green' : 'red'">
          {{ record.last_report_time ? '已报备' : '未报备' }}
        </a-tag>
      </template>
      <template #operations="{ record }">
        <a-button
          type="primary"
          size="mini"
          @click="handleOpenReportDrawer(record)"
        >
          {{ $t('report.records') }}
        </a-button>
      </template>
    </a-table>
  </a-drawer>

  <AddCustomerDrawer
    :parent="parentCustomer"
    :visible="addCustomerDrawerVisible"
    @update:visible="addCustomerDrawerVisible = $event"
    @success="handleAddCustomerSuccess"
  />
</template>

<script setup lang="ts">
  import { ref, reactive, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { queryCustomerList } from '@/api/customer';
  import type { teamRecord } from '@/api/team';
  import type { BaseCustomer } from '@/types/customer';
  import { useDrawerStore } from '@/store';

  interface Props {
    drawerVisible: boolean;
    memberInfo?: teamRecord | null;
  }

  interface Emits {
    (e: 'update:drawerVisible', value: boolean): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();
  const { t } = useI18n();

  const drawerStore = useDrawerStore();

  const loading = ref(false);
  const customerData = ref<BaseCustomer[]>([]);
  const pagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const memberDescriptions = computed(() => [
    { label: '昵称', value: props.memberInfo?.nickname || '-' },
    { label: '手机号', value: props.memberInfo?.mobile || '-' },
    { label: '客户数量', value: String(props.memberInfo?.customer_count || 0) },
  ]);

  const columns = computed(() => [
    { title: '序号', slotName: 'index', width: 60 },
    { title: t('table.column.nickname'), dataIndex: 'nickname' },
    { title: t('table.column.mobile'), dataIndex: 'mobile' },
    { title: t('table.column.city'), dataIndex: 'city' },
    {
      title: t('table.column.isReport'),
      dataIndex: 'last_report_time',
      slotName: 'isReport',
    },
    {
      title: t('table.column.last_report_time'),
      dataIndex: 'last_report_time',
    },
    {
      title: t('table.column.operations'),
      dataIndex: 'operations',
      slotName: 'operations',
    },
  ]);

  const fetchCustomerList = async () => {
    if (!props.memberInfo?.id) return;
    loading.value = true;
    try {
      const res = await queryCustomerList({
        page: pagination.current,
        pageSize: pagination.pageSize,
        id: props.memberInfo.id,
      } as any);
      customerData.value = res.data.list || [];
      pagination.total = res.data.total || 0;
    } catch (error) {
      console.error('获取客户列表失败:', error);
    } finally {
      loading.value = false;
    }
  };

  const handlePageChange = (page: number) => {
    pagination.current = page;
    fetchCustomerList();
  };

  const closeDrawer = () => {
    emit('update:drawerVisible', false);
  };

  const addCustomerDrawerVisible = ref(false);
  const parentCustomer = computed(
    () => props.memberInfo as unknown as BaseCustomer
  );

  const showAddCustomerDrawer = async () => {
    addCustomerDrawerVisible.value = true;
  };

  const handleAddCustomerSuccess = () => {
    fetchCustomerList();
  };

  const handleOpenReportDrawer = (record: BaseCustomer) => {
    drawerStore.openReportDrawer(record);
  };

  watch(
    () => props.drawerVisible,
    (visible) => {
      if (visible && props.memberInfo?.id) {
        pagination.current = 1;
        fetchCustomerList();
      }
    }
  );
</script>

<style scoped>
  .circle {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: rgb(var(--danger-6));
    margin-right: 4px;
  }
  .circle.pass {
    background-color: rgb(var(--success-6));
  }
</style>
